import { z } from 'zod';
import { LABELS } from '../../constants';
import { requiredFieldError, requiredString, validateMobileAndPhone, validateRequiredMobile } from '../../helper';
import { EnterpriseStructure, OwnershipType } from '../../types';
import { baseIdentification } from '../identification';
import { address } from './address';
import { applicantName, baseResidentialApplicant } from './applicant';
import { baseLicense } from './identification';

const {
  OWNERSHIP_PERCENTAGE,
  EMAIL,
  CORPORATE_ENTITIES_LABEL,
  NUMBER_OF_DIRECTORS_LABEL,
  LICENCE_NUMBER,
  EMAIL_ADDRESS,
  PHONE_NUMBER,
} = LABELS;

const requiredCheckbox = z
  .string({ required_error: requiredFieldError() })
  .min(1, { message: requiredFieldError() });

export const enterpriseIdentification = baseResidentialApplicant
  .pick({
    firstName: true,
    middleName: true,
    lastName: true,
    address: true,
    dateOfBirth: true,
  })
  .merge(baseIdentification)
  .extend({
    privacyAndCreditStatement: z.string().optional(),
  });

export const baseOwnershipDetails = z.object({
  structure: z.nativeEnum(EnterpriseStructure),
});

export const companyProprietary = z.object({
  ownershipType: z.literal(OwnershipType.Proprietary),
  directors: z.array(applicantName).min(1, requiredFieldError()),
  beneficialOwners: z
    .array(
      z
        .object({
          ownershipPercentage: z
            .number()
            .min(25, `${OWNERSHIP_PERCENTAGE} should be 25% or more`)
            .max(100, `${OWNERSHIP_PERCENTAGE} should less than or equal to 100%`),
        })
        .and(enterpriseIdentification),
    )
    .min(1, requiredFieldError()),
});

export const companyPublic = z.object({
  ownershipType: z.literal(OwnershipType.Public),
});

export const companyOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.Company),
  company: z.discriminatedUnion('ownershipType', [companyProprietary, companyPublic], {
    errorMap: () => ({
      message: requiredFieldError(),
    }),
  }),
});

export const incorporatedAssociationOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.IncorporatedAssociation),
  incorporatedAssociation: z.object({
    chairman: z.array(enterpriseIdentification),
    secretary: z.array(enterpriseIdentification),
    treasurer: z.array(enterpriseIdentification),
  }),
});

export const unincorporatedAssociationOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.UnincorporatedAssociation),
  unincorporatedAssociation: z.object({
    chairman: z.array(enterpriseIdentification),
    secretary: z.array(enterpriseIdentification),
    treasurer: z.array(enterpriseIdentification),
  }),
});

const smeLicence = baseLicense.extend({
  number: baseLicense.shape.number.pipe(
    z
      .string({ required_error: requiredFieldError(LICENCE_NUMBER) })
      .min(1, requiredFieldError(LICENCE_NUMBER))
      .max(10, 'Incorrect license number format'),
  ),
});

export const smeDirectorWithLicence = applicantName.extend({
  email: requiredString(EMAIL).email(),
  phone: validateRequiredMobile,
  drivingLicense: smeLicence,
  dateOfBirth: baseResidentialApplicant.shape.dateOfBirth,
  address: address,
  privacyAndCreditStatement: requiredCheckbox,
});

export const smeDirectorBasic = applicantName.extend({
  email: requiredString(EMAIL).email(),
  phone: validateRequiredMobile,
  dateOfBirth: baseResidentialApplicant.shape.dateOfBirth,
  address: address,
  isGuarantor: z.boolean().nullish(),
  privacyAndCreditStatement: requiredCheckbox,
});

const smeBeneficialOwner = z
  .object({
    ownershipPercentage: z
      .number({
        required_error: requiredFieldError(OWNERSHIP_PERCENTAGE),
        invalid_type_error: requiredFieldError(OWNERSHIP_PERCENTAGE),
      })
      .min(10, `${OWNERSHIP_PERCENTAGE} should be 10% or more`)
      .max(100, `${OWNERSHIP_PERCENTAGE} should be less than or equal to 100%`),
    email: requiredString(EMAIL_ADDRESS).email(),
    phone: requiredString(PHONE_NUMBER).pipe(validateMobileAndPhone),
    privacyAndCreditStatement: requiredCheckbox,
  })
  .and(enterpriseIdentification);

export const partnershipIndividual = z.object({
  ownershipType: z.literal(OwnershipType.Individual),
  directors: z.array(smeDirectorWithLicence).min(1, requiredFieldError(NUMBER_OF_DIRECTORS_LABEL)),
});

export const partnershipCorporate = z.object({
  ownershipType: z.literal(OwnershipType.Corporate),
  corporateEntities: requiredString(CORPORATE_ENTITIES_LABEL),
  directors: z.array(smeDirectorBasic).min(1, requiredFieldError(NUMBER_OF_DIRECTORS_LABEL)),
  beneficialOwners: z.array(smeBeneficialOwner).nullish(),
});

export const partnershipOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.Partnership),
  partnership: z.discriminatedUnion('ownershipType', [partnershipIndividual, partnershipCorporate], {
    errorMap: () => ({
      message: requiredFieldError('Ownership type'),
    }),
  }),
});

export const proprietaryLimitedCompanyOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.ProprietaryLimitedCompany),
  proprietaryLimitedCompany: z.object({
    directors: z.array(smeDirectorBasic).min(1, requiredFieldError(NUMBER_OF_DIRECTORS_LABEL)),
    beneficialOwners: z.array(smeBeneficialOwner).nullish(),
  }),
});

export const trustIndividual = z.object({
  ownershipType: z.literal(OwnershipType.Individual),
  directors: z.array(smeDirectorWithLicence).min(1, requiredFieldError(NUMBER_OF_DIRECTORS_LABEL)),
  beneficialOwners: z.array(smeBeneficialOwner).nullish(),
});

export const trustCorporate = z.object({
  ownershipType: z.literal(OwnershipType.Corporate),
  corporateEntities: requiredString(CORPORATE_ENTITIES_LABEL),
  directors: z.array(smeDirectorBasic).min(1, requiredFieldError(NUMBER_OF_DIRECTORS_LABEL)),
  beneficialOwners: z.array(smeBeneficialOwner).nullish(),
});

export const trustOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.Trust),
  trust: z.discriminatedUnion('ownershipType', [trustIndividual, trustCorporate], {
    errorMap: () => ({
      message: requiredFieldError('Ownership type'),
    }),
  }),
});

export const soleTraderOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.SoleTrader),
  soleTrader: z.object({
    individual: smeDirectorWithLicence,
  }),
});

export const otherOwnershipDetails = baseOwnershipDetails.extend({
  structure: z.literal(EnterpriseStructure.Other),
  other: z.object({
    individual: smeDirectorWithLicence,
  }),
});

export const ownershipDetails = z.discriminatedUnion('structure', [
  companyOwnershipDetails,
  incorporatedAssociationOwnershipDetails,
  unincorporatedAssociationOwnershipDetails,
  partnershipOwnershipDetails,
  proprietaryLimitedCompanyOwnershipDetails,
  trustOwnershipDetails,
  soleTraderOwnershipDetails,
  otherOwnershipDetails,
]);
